import clsx from 'clsx';
import {
  ArrowDown,
  ArrowRight,
  BarChart3,
  Bell,
  Building2,
  Cake,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  CalendarSync,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  ClipboardCheck,
  Columns3,
  Contact,
  Copy,
  FileSpreadsheet,
  Filter,
  Gauge,
  GripVertical,
  Grid3x3,
  History,
  KeyRound,
  Layers,
  LayoutDashboard,
  Link2,
  ListFilter,
  LockKeyhole,
  MapPin,
  Move,
  Megaphone,
  MessageSquareText,
  Moon,
  Palette,
  MousePointerClick,
  PenLine,
  Pin,
  Plane,
  Printer,
  ReceiptText,
  RefreshCw,
  Repeat,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  Tag,
  Timer,
  TriangleAlert,
  Undo2,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { GUIDE_AUDIENCES, defaultGuideAudience, guideTopics, parseGuideParam, visibleGuideAudiences, type GuideAudience, type GuideTopicId } from '@pz/core';
import { Button, Tabs, useToast } from '../components/ui';
import { h } from '../lib/format';
import { useMe } from '../lib/me';
import { HELP, type HelpStep, type HelpText } from './help-content';
import { LEAD_HELP, type LeadHelpText } from './help-lead';
import { EXTRA, type ExtraText } from './help-extra';
import { TOPICS, type TabText, type TopicsText } from './help-topics';

/**
 * Role-based guide in German, English and Turkish (language of the interface). Every topic is written once; each of
 * the four tabs (employees, project lead, office and management, technical admin) shows the topics tagged for it
 * (GUIDE_TOPICS in @pz/core), numbered per tab. The tab of the own role opens first. Workflows as diagrams instead
 * of long texts; the same page is printed to the A4 PDFs handed out with the rollout
 * (?guide=employee, lead, office or admin; the old ?guide=team still works).
 */

/** `**bold**` inside guide texts. */
function rich(text: string): ReactNode {
  return text.split('**').map((part, i) => (i % 2 ? <strong key={i} className="text-ink">{part}</strong> : <Fragment key={i}>{part}</Fragment>));
}

function Section({ id, n, title, lead, children, className }: { id?: string; n: number; title: string; lead?: string; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={clsx('scroll-mt-4 break-inside-avoid rounded-xl border border-line bg-sheet p-5 print:rounded-none print:border-0 print:border-t print:px-0 print:py-3', className)}>
      <div className="mb-4 flex items-baseline gap-3 print:mb-3">
        <span className="num inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-ink">{n}</span>
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {lead && <p className="text-sm text-graphite">{lead}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

/** Steps with arrows: horizontal on wide screens and on paper, vertical on phones. */
function Flow({ steps, icons, tone = 'accent' }: { steps: HelpStep[]; icons: LucideIcon[]; tone?: 'accent' | 'soft' }) {
  return (
    <ol className="flex flex-col items-stretch gap-2 md:flex-row md:items-stretch print:flex-row">
      {steps.map((s, i) => {
        const Icon = icons[i];
        return (
          <li key={s.title} className="flex flex-1 flex-col items-center gap-2 md:flex-row md:items-stretch print:flex-row print:items-stretch">
            <div className={clsx('flex h-full w-full flex-col gap-1.5 rounded-lg border p-3', tone === 'accent' ? 'border-accent/30 bg-accent-soft/60' : 'border-line bg-sheet-2')}>
              <div className="flex items-center gap-2">
                <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-ink">
                  <Icon className="size-4" />
                </span>
                <span className="text-sm leading-tight font-semibold">{s.title}</span>
              </div>
              <p className="text-[13px] leading-snug text-graphite">{rich(s.text)}</p>
              {s.who && <span className="mt-auto self-start rounded-full bg-sheet px-2 py-0.5 text-[11px] font-medium text-accent ring-1 ring-accent/30">{s.who}</span>}
            </div>
            {i < steps.length - 1 && (
              <>
                <ArrowRight className="hidden size-5 shrink-0 self-center text-accent md:block print:block" aria-hidden />
                <ArrowDown className="size-5 shrink-0 text-accent md:hidden print:hidden" aria-hidden />
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Status path of a time entry. */
function StatusDiagram({ c }: { c: HelpText['s4'] }) {
  const box = (x: number, y: number, w: number, [label, sub]: [string, string], fill: string, stroke: string) => (
    <g>
      <rect x={x} y={y} width={w} height={54} rx={10} fill={fill} stroke={stroke} strokeWidth={1.5} />
      <text x={x + w / 2} y={y + 23} textAnchor="middle" fontSize={14} fontWeight={600} fill="var(--ink)">
        {label}
      </text>
      <text x={x + w / 2} y={y + 41} textAnchor="middle" fontSize={11} fill="var(--graphite)">
        {sub}
      </text>
    </g>
  );
  const arrow = (x1: number, y1: number, x2: number, y2: number, label: string, lx: number, ly: number) => (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--accent)" strokeWidth={2} markerEnd="url(#pz-arrow)" />
      <text x={lx} y={ly} textAnchor="middle" fontSize={11} fill="var(--graphite)">
        {label}
      </text>
    </g>
  );
  return (
    <svg viewBox="0 0 980 230" className="w-full" role="img" aria-label={c.aria}>
      <defs>
        <marker id="pz-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--accent)" />
        </marker>
        <marker id="pz-arrow-bad" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--bad)" />
        </marker>
      </defs>
      {box(10, 40, 150, c.draft, 'var(--sheet-2)', 'var(--line-strong)')}
      {box(210, 40, 150, c.submitted, 'var(--info-soft)', 'var(--info)')}
      {box(410, 40, 170, c.l1, 'var(--accent-soft)', 'var(--accent)')}
      {box(630, 40, 150, c.approved, 'var(--good-soft)', 'var(--good)')}
      {box(830, 40, 140, c.billed, 'var(--good-soft)', 'var(--good)')}
      {arrow(160, 67, 208, 67, c.arrows.submit, 184, 30)}
      {arrow(360, 67, 408, 67, c.arrows.lead, 384, 30)}
      {arrow(580, 67, 628, 67, c.arrows.office, 604, 30)}
      {arrow(780, 67, 828, 67, c.arrows.close, 804, 30)}
      <g>
        <rect x={290} y={160} width={220} height={54} rx={10} fill="var(--bad-soft)" stroke="var(--bad)" strokeWidth={1.5} />
        <text x={400} y={183} textAnchor="middle" fontSize={14} fontWeight={600} fill="var(--ink)">
          {c.rejected[0]}
        </text>
        <text x={400} y={201} textAnchor="middle" fontSize={11} fill="var(--graphite)">
          {c.rejected[1]}
        </text>
      </g>
      <path d="M 300 95 L 330 158" stroke="var(--bad)" strokeWidth={2} fill="none" markerEnd="url(#pz-arrow-bad)" />
      <path d="M 480 95 L 460 158" stroke="var(--bad)" strokeWidth={2} fill="none" markerEnd="url(#pz-arrow-bad)" />
      <path d="M 290 187 C 180 187 95 150 90 97" stroke="var(--bad)" strokeWidth={2} strokeDasharray="5 4" fill="none" markerEnd="url(#pz-arrow-bad)" />
      <text x={160} y={200} textAnchor="middle" fontSize={11} fill="var(--bad)">
        {c.arrows.fix}
      </text>
    </svg>
  );
}

/** "Where do I book this?" decision tree. */
function DecisionTree({ c }: { c: HelpText['s3'] }) {
  const node = (x: number, y: number, w: number, hh: number, text: [string, string], kind: 'q' | 'a') => (
    <g>
      <rect x={x} y={y} width={w} height={hh} rx={kind === 'q' ? 26 : 10} fill={kind === 'q' ? 'var(--sheet-2)' : 'var(--accent-soft)'} stroke={kind === 'q' ? 'var(--line-strong)' : 'var(--accent)'} strokeWidth={1.5} />
      {text.map((line, i) => (
        <text key={i} x={x + w / 2} y={y + hh / 2 + (i - (text.length - 1) / 2) * 16 + 5} textAnchor="middle" fontSize={13} fontWeight={i === 0 ? 600 : 400} fill="var(--ink)">
          {line}
        </text>
      ))}
    </g>
  );
  const line = (d: string, label: string, lx: number, ly: number) => (
    <g>
      <path d={d} stroke="var(--accent)" strokeWidth={2} fill="none" markerEnd="url(#pz-arrow2)" />
      <text x={lx} y={ly} fontSize={12} fontWeight={600} fill="var(--accent)">
        {label}
      </text>
    </g>
  );
  return (
    <svg viewBox="0 0 980 300" className="w-full" role="img" aria-label={c.aria}>
      <defs>
        <marker id="pz-arrow2" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--accent)" />
        </marker>
      </defs>
      {node(20, 20, 330, 60, c.q1, 'q')}
      {node(420, 20, 540, 60, c.a1, 'a')}
      {line('M 350 50 L 416 50', c.yes, 368, 42)}
      {node(20, 130, 330, 60, c.q2, 'q')}
      {line('M 170 80 L 170 126', c.no, 180, 108)}
      {node(420, 130, 540, 60, c.a2, 'a')}
      {line('M 350 160 L 416 160', c.yes, 368, 152)}
      {node(420, 230, 540, 60, c.a3, 'a')}
      {line('M 170 190 L 170 260 L 416 260', c.no, 180, 225)}
    </svg>
  );
}

function RoleMatrix({ c }: { c: LeadHelpText['m1'] }) {
  return (
    <div className="scroll-x">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-line">
            <th className="py-2 pr-3 text-left text-xs font-medium text-graphite">{c.task}</th>
            {c.roles.map((r) => (
              <th key={r} className="px-2 py-2 text-center text-xs font-medium text-graphite">
                {r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {c.rows.map(([task, cells]) => (
            <tr key={task} className="border-b border-line/60">
              <td className="py-1.5 pr-3">{task}</td>
              {cells.map((x, i) => (
                <td key={i} className="px-2 py-1.5 text-center">
                  {x ? <Check className="mx-auto size-4 text-good" aria-label={c.yes} /> : <span className="text-faint" aria-label={c.no}>·</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-graphite">{c.note}</p>
    </div>
  );
}

function WeekRhythm({ c }: { c: HelpText['s5'] }) {
  const icons = [Timer, Send, ClipboardCheck, CheckCheck, CalendarCheck];
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-5 print:grid-cols-5">
      {c.days.map((x, i) => {
        const Icon = icons[i];
        return (
          <div key={x.d} className="relative flex flex-col gap-1 rounded-lg border border-line bg-sheet-2 p-3">
            <span className="text-[11px] font-semibold tracking-wide text-accent uppercase">{x.d}</span>
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <Icon className="size-4 shrink-0 text-accent" />
              {x.t}
            </span>
            <span className="text-xs text-graphite">{x.s}</span>
            {i < c.days.length - 1 && <ArrowRight className="absolute top-1/2 -right-3.5 z-10 hidden size-5 -translate-y-1/2 rounded-full bg-sheet text-accent sm:block print:block" aria-hidden />}
          </div>
        );
      })}
    </div>
  );
}

/** Independent pages side by side (no order implied, so no arrows). */
function Tiles({ steps, icons }: { steps: HelpStep[]; icons: LucideIcon[] }) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 print:grid-cols-4">
      {steps.map((s, i) => {
        const Icon = icons[i];
        return (
          <li key={s.title} className="flex flex-col gap-1.5 rounded-lg border border-line bg-sheet-2 p-3">
            <span className="flex items-center gap-2">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-ink">
                <Icon className="size-4" />
              </span>
              <span className="text-sm leading-tight font-semibold">{s.title}</span>
            </span>
            <span className="text-[13px] leading-snug text-graphite">{rich(s.text)}</span>
            {s.who && <span className="mt-auto self-start rounded-full bg-sheet px-2 py-0.5 text-[11px] font-medium text-accent ring-1 ring-accent/30">{s.who}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function Cards({ items, icons }: { items: { title: string; text: string }[]; icons: LucideIcon[] }) {
  return (
    <ul className="grid gap-2 text-sm sm:grid-cols-3 print:grid-cols-3">
      {items.map((x, i) => {
        const Icon = icons[i];
        return (
          <li key={x.title} className="flex gap-2 rounded-md border border-line bg-sheet-2 p-3">
            <Icon className="mt-0.5 size-4 shrink-0 text-accent" />
            <span>
              <strong>{x.title}:</strong> {rich(x.text)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Notices({ c, tol }: { c: HelpText['s7']; tol: string }) {
  const items: { tone: string; icon: ReactNode; t: [string, string] }[] = [
    { tone: 'bg-bad-soft text-bad', icon: <ChevronDown className="size-4" />, t: c.bad },
    { tone: 'bg-warn-soft text-warn', icon: <ChevronUp className="size-4" />, t: c.warn },
    { tone: 'bg-good-soft text-good', icon: <Sparkles className="size-4" />, t: c.good },
  ];
  return (
    <div className="flex flex-col gap-2">
      <ul className="grid gap-2 sm:grid-cols-3 print:grid-cols-3">
        {items.map((x) => (
          <li key={x.t[0]} className={clsx('flex flex-col gap-1 rounded-md px-3 py-2 text-sm', x.tone)}>
            <span className="flex items-center gap-1.5 font-semibold">
              {x.icon}
              {x.t[0]}
            </span>
            <span className="text-ink">{x.t[1]}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-graphite">{c.note.replace('{{tol}}', tol)}</p>
    </div>
  );
}

function PlanExample({ c }: { c: HelpText['s8'] }) {
  const icons = [Layers, CheckCheck, ReceiptText, BarChart3];
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] print:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div className="flex flex-col gap-3 rounded-lg border border-line bg-sheet-2 p-4">
        <div className="text-sm font-semibold">{c.example.title}</div>
        <div className="flex flex-col gap-2 text-sm">
          <div className="grid grid-cols-[5rem_minmax(0,1fr)_3.5rem] items-center gap-2">
            <span className="text-graphite">{c.example.plan}</span>
            <span className="h-3 rounded-r bg-viz-soft" style={{ width: '100%' }} />
            <span className="num text-right">10 h</span>
          </div>
          <div className="grid grid-cols-[5rem_minmax(0,1fr)_3.5rem] items-center gap-2">
            <span className="text-graphite">{c.example.booked}</span>
            <span className="h-3 rounded-r bg-viz-strong" style={{ width: '40%' }} />
            <span className="num text-right">4 h</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-md bg-good-soft px-3 py-2 text-sm font-semibold text-good">
          <Sparkles className="size-4" /> {c.example.result}
        </div>
      </div>
      <ul className="flex flex-col gap-2 text-sm">
        {c.points.map((p, i) => {
          const Icon = icons[i];
          return (
            <li key={i} className="flex gap-2">
              <Icon className="mt-0.5 size-4 shrink-0 text-accent" />
              <span>{rich(p)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Symbols({ c, tol }: { c: HelpText['s11']; tol: string }) {
  const items: { icon: ReactNode; text: string }[] = [
    { icon: <ChevronDown className="size-4 text-bad" />, text: c.under.replace('{{tol}}', tol) },
    { icon: <ChevronUp className="size-4 text-warn" />, text: c.over.replace('{{tol}}', tol) },
    { icon: <Sparkles className="size-4 text-good" />, text: c.fast },
    { icon: <TriangleAlert className="size-4 text-warn" />, text: c.forma },
    { icon: <MapPin className="size-4 text-graphite" />, text: c.location },
    { icon: <span className="inline-block h-3 w-5 rounded-sm hatch" />, text: c.hatch },
  ];
  return (
    <ul className="grid gap-2 sm:grid-cols-2 print:grid-cols-2">
      {items.map((x) => (
        <li key={x.text} className="flex items-start gap-2.5 rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm">
          <span className="mt-0.5 inline-flex min-w-6 justify-center">{x.icon}</span>
          <span>{x.text}</span>
        </li>
      ))}
    </ul>
  );
}

export function HelpPage() {
  const { i18n } = useTranslation();
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const lang = (['de', 'en', 'tr'] as const).find((l) => i18n.language.startsWith(l)) ?? 'de';
  const x = TOPICS[lang];
  // the tab of the own role opens first; the other tabs by the role hierarchy (request 30 of round 4): externals and
  // employees read their own guide, project leads also the ones below, office, management and administration all.
  // The tab is part of the address: back and forward work; a tab the person may not open falls back to the own one
  const own = defaultGuideAudience(me.permissions, me.leadProjectIds.length > 0, me.kind === 'external' ? 'external' : 'employee');
  const visible = visibleGuideAudiences(own);
  const asked = parseGuideParam(params.get('guide'));
  const audience = asked && visible.includes(asked) ? asked : own;
  return (
    <div lang={lang} className="mx-auto flex max-w-5xl flex-col gap-4 print:max-w-none print:gap-2">
      <div className={visible.length > 1 ? 'print:hidden' : 'hidden'}>
        <Tabs
          value={audience}
          onChange={(v) => setParams(v === own ? {} : { guide: v })}
          tabs={GUIDE_AUDIENCES.filter((a) => visible.includes(a)).map((a) => ({
            id: a,
            label: (
              <>
                {x.tabs[a].label}
                {a === own && <span className="rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent">{x.ownRole}</span>}
              </>
            ),
          }))}
        />
      </div>
      <Guide audience={audience} lang={lang} />
    </div>
  );
}

function GuideHeader({ tab, print }: { tab: TabText; print: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="text-xs font-semibold tracking-wide text-accent uppercase">{tab.kicker}</p>
        <h1 className="text-2xl font-semibold">{tab.title}</h1>
        <p className="text-sm text-graphite">{tab.subtitle}</p>
        <p className="mt-1 max-w-[80ch] text-xs text-graphite">{tab.hint}</p>
      </div>
      <Button
        icon={<Printer className="size-4" />}
        className="print:hidden"
        onClick={() => {
          window.print();
          // the demo viewer may block the print dialog without any message
          if (__DEMO__) toast.info(t('common.demoPrint'));
        }}
      >
        {print}
      </Button>
    </header>
  );
}

function Banner({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex gap-3 rounded-xl border border-accent/30 bg-accent-soft/50 px-4 py-3 text-sm break-inside-avoid">
      <ShieldCheck className="mt-0.5 size-5 shrink-0 text-accent" />
      <span>
        <strong>{title}</strong> {text}
      </span>
    </div>
  );
}

function Points({ items, icons }: { items: string[]; icons: LucideIcon[] }) {
  return (
    <ul className="mt-3 flex flex-col gap-1.5 text-sm text-graphite">
      {items.map((p, i) => {
        const Icon = icons[i % icons.length];
        return (
          <li key={i} className="flex items-start gap-2">
            <Icon className="mt-0.5 size-4 shrink-0 text-accent" />
            <span>{rich(p)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Jump list of a long tab (not printed). */
function Contents({ label, items }: { label: string; items: { id: string; title: string }[] }) {
  const go = (id: string) => {
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`guide-${id}`)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };
  return (
    <nav aria-label={label} className="print:hidden">
      <ol className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <li key={it.id}>
            <button type="button" onClick={() => go(it.id)} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-sheet px-2.5 py-1 text-xs hover:border-accent hover:text-accent">
              <span className="num font-semibold text-accent">{i + 1}</span>
              {it.title}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

interface TopicContext {
  c: HelpText;
  l: LeadHelpText;
  x: TopicsText;
  /** topics of the functions added in round 4 (help-extra.ts) */
  e: ExtraText;
  tol: string;
  /** calendar day from which a sick note is expected (0: not expected) */
  docDay: number;
}

/** Title, lead and body of one topic; the texts come from help-content.ts, help-lead.ts and help-topics.ts. */
function topicView(id: GuideTopicId, { c, l, x, e, tol, docDay }: TopicContext): { title: string; lead?: string; body: ReactNode } {
  switch (id) {
    case 'home':
      return {
        title: x.home.title,
        lead: x.home.lead,
        body: (
          <>
            <Tiles steps={x.home.items} icons={[LayoutDashboard, CalendarClock, Megaphone, Bell]} />
            <Points items={[x.home.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    case 'external':
      return {
        title: x.external.title,
        lead: x.external.lead,
        body: (
          <>
            <Tiles steps={x.external.items} icons={[Layers, Timer, ClipboardCheck, CircleHelp]} />
            <Points items={[x.external.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    case 'glance':
      return { title: c.s1.title, lead: c.s1.lead, body: <Flow steps={c.s1.steps} icons={[Layers, Timer, ClipboardCheck, CalendarRange, BarChart3]} /> };
    case 'record':
      return {
        title: c.s2.title,
        lead: c.s2.lead,
        body: (
          <>
            <Flow tone="soft" steps={c.s2.steps} icons={[MousePointerClick, Filter, Search, CalendarDays]} />
            <p className="mt-3 flex items-start gap-2 text-sm text-graphite">
              <ReceiptText className="mt-0.5 size-4 shrink-0 text-accent" /> <span>{rich(c.s2.billing)}</span>
            </p>
            <p className="mt-1.5 flex items-start gap-2 text-sm text-graphite">
              <Send className="mt-0.5 size-4 shrink-0 text-accent" /> <span>{rich(c.s2.submit)}</span>
            </p>
            <p className="mt-1.5 flex items-start gap-2 text-sm text-graphite">
              <Undo2 className="mt-0.5 size-4 shrink-0 text-accent" /> <span>{rich(c.s2.undo)}</span>
            </p>
          </>
        ),
      };
    case 'quick':
      return {
        title: x.quick.title,
        lead: x.quick.lead,
        body: (
          <>
            <Tiles steps={x.quick.items} icons={[History, Star, CalendarRange, Copy]} />
            <Points items={[x.quick.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    case 'where':
      return { title: c.s3.title, body: <DecisionTree c={c.s3} /> };
    case 'path':
      return { title: c.s4.title, lead: c.s4.lead, body: <StatusDiagram c={c.s4} /> };
    case 'rhythm':
      return {
        title: c.s5.title,
        body: (
          <>
            <WeekRhythm c={c.s5} />
            <p className="mt-3 flex items-start gap-2 text-sm text-graphite">
              <CalendarPlus className="mt-0.5 size-4 shrink-0 text-accent" /> <span>{rich(c.s5.weekend)}</span>
            </p>
          </>
        ),
      };
    case 'absence': {
      const items = c.s6.items.map((item, i) => (i === 1 && docDay > 0 ? { ...item, text: `${item.text} ${c.s6.sickDoc.replace('{{docDay}}', String(docDay))}` } : item));
      return {
        title: c.s6.title,
        lead: c.s6.lead,
        body: (
          <>
            <Cards items={items} icons={[Plane, Stethoscope, Users]} />
            <Points items={[c.s6.change]} icons={[CalendarClock]} />
          </>
        ),
      };
    }
    case 'signals':
      return { title: c.s7.title, lead: c.s7.lead, body: <Notices c={c.s7} tol={tol} /> };
    case 'planactual':
      return { title: c.s8.title, lead: c.s8.lead, body: <PlanExample c={c.s8} /> };
    case 'symbols':
      return { title: c.s11.title, body: <Symbols c={c.s11} tol={tol} /> };
    case 'help':
      return { title: c.s12.title, body: <Cards items={c.s12.items} icons={[KeyRound, Undo2, CircleHelp]} /> };
    case 'roles':
      return { title: l.m1.title, lead: l.m1.lead, body: <RoleMatrix c={l.m1} /> };
    case 'approvals':
      return {
        title: l.m2.title,
        lead: l.m2.lead,
        body: (
          <>
            <Flow steps={l.m2.steps} icons={[ClipboardCheck, CheckCheck, Undo2]} />
            <Points items={l.m2.points} icons={[Undo2, CalendarCheck, FileSpreadsheet]} />
          </>
        ),
      };
    case 'hr':
      return { title: l.m3.title, lead: l.m3.lead, body: <Tiles steps={l.m3.steps} icons={[Plane, Stethoscope, Timer, CalendarDays]} /> };
    case 'people':
      return {
        title: x.people.title,
        lead: x.people.lead,
        body: (
          <>
            <Tiles steps={x.people.items} icons={[Users, KeyRound, Tag, Cake]} />
            <Points items={[x.people.note]} icons={[CircleHelp]} />
          </>
        ),
      };
    case 'planning':
      return {
        title: l.m4.title,
        lead: l.m4.lead,
        body: (
          <>
            <Flow tone="soft" steps={l.m4.steps} icons={[Layers, CalendarRange, Gauge]} />
            <Points items={[l.m4.note]} icons={[TriangleAlert]} />
          </>
        ),
      };
    case 'review':
      return {
        title: l.m5.title,
        lead: l.m5.lead,
        body: (
          <>
            <Cards items={l.m5.items} icons={[Timer, ReceiptText, Sparkles, Send, ChevronDown]} />
            <Points items={[l.m5.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    case 'analysis':
      return {
        title: l.ma.title,
        lead: l.ma.lead,
        body: (
          <>
            <Cards items={l.ma.items} icons={[Gauge, BarChart3, Building2, Grid3x3, CalendarRange, Users, FileSpreadsheet]} />
            <Points items={[l.ma.note]} icons={[CircleHelp]} />
          </>
        ),
      };
    case 'billing':
      return {
        title: l.m6.title,
        lead: l.m6.lead,
        body: (
          <>
            <Flow steps={l.m6.steps} icons={[ReceiptText, Filter, Check, CheckCheck]} />
            <Points items={[l.m6.abschlag, ...l.m6.points]} icons={[Layers, KeyRound, Users, MessageSquareText, FileSpreadsheet]} />
          </>
        ),
      };
    case 'customers':
      return {
        title: x.customers.title,
        lead: x.customers.lead,
        body: (
          <>
            <Cards items={x.customers.items} icons={[Contact, Building2, ReceiptText]} />
            <Points items={[x.customers.note]} icons={[CircleHelp]} />
          </>
        ),
      };
    case 'closing':
      return { title: l.m7.title, lead: l.m7.lead, body: <Flow tone="soft" steps={l.m7.steps} icons={[Send, CheckCheck, CalendarCheck, ReceiptText, Timer]} /> };
    case 'publish':
      return {
        title: x.publish.title,
        lead: x.publish.lead,
        body: (
          <>
            <Flow steps={x.publish.steps} icons={[PenLine, Users, CalendarClock, CheckCheck]} />
            <Points items={x.publish.points} icons={[Link2, Pin]} />
          </>
        ),
      };
    case 'forma':
      return { title: l.m8.title, lead: l.m8.lead, body: <Cards items={l.m8.items} icons={[Timer, Filter, TriangleAlert, ListFilter, Columns3]} /> };
    case 'settings':
      return { title: l.m9.title, body: <Cards items={l.m9.items} icons={[KeyRound, Layers, ShieldCheck]} /> };
    case 'automation':
      return {
        title: x.automation.title,
        lead: x.automation.lead,
        body: (
          <>
            <Cards items={x.automation.items} icons={[RefreshCw, CalendarSync, Moon, Bell, Repeat, ShieldCheck]} />
            <Points items={[x.automation.note]} icons={[CircleHelp]} />
          </>
        ),
      };
    case 'routine':
      return {
        title: e.routine.title,
        lead: e.routine.lead,
        body: (
          <>
            <Cards items={e.routine.items} icons={[Repeat, CalendarCheck, GripVertical, Move, ListFilter]} />
            <Points items={[e.routine.note]} icons={[CircleHelp]} />
          </>
        ),
      };
    case 'leavedays':
      return {
        title: e.leavedays.title,
        lead: e.leavedays.lead,
        body: (
          <>
            <Cards items={e.leavedays.items} icons={[CalendarDays, Stethoscope, CalendarRange, Users]} />
            <Points items={[e.leavedays.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    case 'projectrules':
      return {
        title: e.projectrules.title,
        lead: e.projectrules.lead,
        body: (
          <>
            <Cards items={e.projectrules.items} icons={[ClipboardCheck, Copy, PenLine, Building2, Palette]} />
            <Points items={[e.projectrules.note]} icons={[Undo2]} />
          </>
        ),
      };
    case 'hrtools':
      return {
        title: e.hrtools.title,
        lead: e.hrtools.lead,
        body: (
          <>
            <Cards items={e.hrtools.items} icons={[Timer, Stethoscope, CalendarDays, CalendarPlus, CalendarClock]} />
            <Points items={[e.hrtools.note]} icons={[TriangleAlert]} />
          </>
        ),
      };
    case 'protection':
      return {
        title: e.protection.title,
        lead: e.protection.lead,
        body: (
          <>
            <Cards items={e.protection.items} icons={[UserCog, LockKeyhole, CircleHelp]} />
            <Points items={[e.protection.note]} icons={[TriangleAlert]} />
          </>
        ),
      };
    case 'staffing':
      return {
        title: e.staffing.title,
        lead: e.staffing.lead,
        body: (
          <>
            <Cards items={e.staffing.items} icons={[ListFilter, Grid3x3, Layers, Gauge]} />
            <Points items={[e.staffing.note]} icons={[Building2]} />
          </>
        ),
      };
    case 'reports':
      return {
        title: e.reports.title,
        lead: e.reports.lead,
        body: (
          <>
            <Cards items={e.reports.items} icons={[Moon, ClipboardCheck, FileSpreadsheet, Bell]} />
            <Points items={[e.reports.note]} icons={[ShieldCheck]} />
          </>
        ),
      };
    default: {
      // a topic in GUIDE_TOPICS without a view is a compile error
      const missing: never = id;
      throw new Error(`no guide view for ${String(missing)}`);
    }
  }
}

/** One tab: the topics tagged for the audience, numbered 1…n. */
function Guide({ audience, lang }: { audience: GuideAudience; lang: 'de' | 'en' | 'tr' }) {
  const { me } = useMe();
  const c = HELP[lang];
  const l = LEAD_HELP[lang];
  const x = TOPICS[lang];
  const e = EXTRA[lang];
  const tol = h(me.settings.weekToleranceMinutes, 0);
  // the sentence on the sick note only when the company expects one (setting, calendar day > 0)
  const docDay = me.settings.leaveDocumentFromDay?.sick ?? 0;
  const topics = guideTopics(audience).map((id) => ({ id, ...topicView(id, { c, l, x, e, tol, docDay }) }));
  return (
    <>
      <GuideHeader tab={x.tabs[audience]} print={c.print} />
      {audience === 'employee' ? <Banner title={c.privacy.title} text={c.privacy.text} /> : <Banner title={l.confidential.title} text={l.confidential.text} />}
      {topics.length > 6 && <Contents label={x.contents} items={topics} />}
      {topics.map((tp, i) => (
        <Section key={tp.id} id={`guide-${tp.id}`} n={i + 1} title={tp.title} lead={tp.lead} className={i === topics.length - 1 ? 'mb-6 print:mb-0' : undefined}>
          {tp.body}
        </Section>
      ))}
    </>
  );
}
